import { PageRoute } from "../models/enums";
import { buildBaseUrl } from "../services/utils";
import { getImageLink, getImageUrl } from "../services/asset";
import { findMake, findModel } from "../routes/Configuration";
import { MakeI } from "../typings/configurationTypes";

// Fallback used in the JSON-LD `image` field for ads with no uploaded
// photos — without it Google's Rich Results Test flags the Product as
// invalid ("Missing field 'image'") and refuses to show the rich card.
// Matches the brand fallback used by HelmetHelper for og:image.
const defaultProductImage = getImageUrl("og-image.jpg");

// Build JSON-LD payload for an ad detail page so Google Discover and
// Search can show rich cards. We emit a Product with Offer (name,
// image, description, price, brand) and merge in Vehicle-specific
// fields (manufacturer, model year, mileage, body type, …) when the
// ad looks like a vehicle listing. BreadcrumbList is included
// separately so the breadcrumb row renders in SERPs.
//
// Returns an array of nodes safe to pass to <HelmetComponent
// structuredData={...}>. Caller guards on summary presence.

interface AdDetailsLike {
  module?: string;
  category?: string;
  routeLinks?: Array<{ title?: string; path?: string }>;
  redirectData?: {
    module?: string;
    category?: string;
    smartLink?: string;
    code?: number | string;
  };
  summary?: any;
}

const buildCanonical = (ad: AdDetailsLike): string | null => {
  const rd = ad.redirectData;
  if (!rd || !rd.module || !rd.category || !rd.smartLink || rd.code == null) {
    return null;
  }
  return buildBaseUrl(
    "/" + rd.module + "/" + rd.category + PageRoute.details + "/" + rd.smartLink + "/" + rd.code
  );
};

const buildImages = (summary: any): string[] => {
  const images: string[] = Array.isArray(summary && summary.images) ? summary.images : [];
  return images.filter(Boolean).map(getImageLink);
};

// Vehicle module in the data model is "vehicles" (EN) and the HR slug
// is "auto-moto"; detail payloads can arrive in either depending on
// endpoint, so accept both.
const isVehicleModule = (module: string | undefined): boolean => {
  if (!module) return false;
  const m = module.toLowerCase();
  return m === "vehicles" || m === "auto-moto";
};

const buildOffer = (summary: any, canonical: string | null) => {
  if (!summary || summary.price == null) return null;
  // BE uses status 1 (active) / 2 (promoted active) for live ads; 3+ are
  // paused/sold/expired. Only live ads are InStock for Schema purposes.
  const inStock = summary.status === 1 || summary.status === 2;
  const offer: any = {
    "@type": "Offer",
    price: summary.price,
    priceCurrency: "EUR",
    availability: inStock
      ? "https://schema.org/InStock"
      : "https://schema.org/OutOfStock",
    itemCondition:
      summary.isNew === 1
        ? "https://schema.org/NewCondition"
        : "https://schema.org/UsedCondition",
  };
  if (canonical) offer.url = canonical;
  return offer;
};

const buildBreadcrumb = (ad: AdDetailsLike, canonical: string | null) => {
  const links = Array.isArray(ad.routeLinks) ? ad.routeLinks : [];
  // Google requires `item` (URL) + `name` on every intermediate crumb;
  // the last (current-page) crumb may omit `item`. Drop any link that
  // can't produce both, otherwise the whole BreadcrumbList is flagged
  // invalid in Rich Results Test.
  const items = links
    .filter(link => link && link.title && link.path)
    .map((link, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: link.title,
      item: buildBaseUrl(link.path),
    }));
  if (canonical && ad.summary && ad.summary.title) {
    items.push({
      "@type": "ListItem",
      position: items.length + 1,
      name: ad.summary.title,
      item: canonical,
    });
  }
  if (items.length === 0) return null;
  return { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: items };
};

export const buildAdDetailStructuredData = (
  ad: AdDetailsLike,
  // Car makes for brand/model name lookup — pass undefined for non-vehicle
  // ads or when the dataset isn't needed. Google's Product/Vehicle rich card
  // likes a `brand` identifier alongside sku; without it Rich Results Test
  // emits a "Nije naveden globalni identifikator (gtin, brend)" warning.
  makes?: MakeI[]
): object[] => {
  const summary = ad && ad.summary;
  if (!summary) return [];

  const canonical = buildCanonical(ad);
  const images = buildImages(summary);
  const offer = buildOffer(summary, canonical);

  // Product + Vehicle (merged into one node for vehicle modules). Google
  // supports a Vehicle type that extends Product, so a single node carries
  // both bags of properties cleanly.
  const base: any = {
    "@context": "https://schema.org",
    "@type": isVehicleModule(ad.module) ? ["Product", "Vehicle"] : "Product",
    name: summary.title,
    description: summary.description,
  };
  base.image = images.length === 0
    ? defaultProductImage
    : (images.length === 1 ? images[0] : images);
  if (canonical) base.url = canonical;
  // Google Product markup requires at least one identifier (sku / mpn /
  // gtin / brand). We don't have GTIN/MPN for classifieds, so always
  // emit sku — use the ad code (short, human-readable, unique) and fall
  // back to the GUID id. Previously only emitted on vehicles, which left
  // non-vehicle categories flagged as "missing field" in Rich Results.
  const sku = summary.code != null ? String(summary.code) : (summary.id ? String(summary.id) : null);
  if (sku) base.sku = sku;
  if (offer) base.offers = offer;

  // Vehicle-specific fields when available on the ad summary. BE serves
  // these inconsistently across modules so we only emit what we have.
  if (isVehicleModule(ad.module)) {
    const make = summary.makeId ? findMake(makes, summary.makeId) : undefined;
    const model = summary.modelId ? findModel(makes, summary.modelId) : undefined;
    if (make && make.name) {
      base.brand = { "@type": "Brand", name: make.name };
      base.manufacturer = { "@type": "Organization", name: make.name };
    }
    if (model && model.name) {
      base.model = model.name;
    }
    if (summary.makeYear) {
      base.vehicleModelDate = String(summary.makeYear).substring(0, 4);
      base.modelDate = String(summary.makeYear).substring(0, 4);
    }
    if (summary.mileage != null) {
      base.mileageFromOdometer = {
        "@type": "QuantitativeValue",
        value: summary.mileage,
        unitCode: "KMT",
      };
    }
    if (summary.power != null) {
      base.vehicleEngine = {
        "@type": "EngineSpecification",
        enginePower: {
          "@type": "QuantitativeValue",
          value: summary.power,
          unitCode: "KWT",
        },
      };
    }
  }

  const breadcrumb = buildBreadcrumb(ad, canonical);
  return breadcrumb ? [base, breadcrumb] : [base];
};



// WEBPACK FOOTER //
// ./src/utils/buildAdDetailStructuredData.ts